import React, { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import broadcastService, { LibraryEntitlement } from '../../../services/broadcastService'

/**
 * Warns a customer that their broadcast allowance is nearly or fully spent.
 *
 * Session-scoped dismiss, following EnhancedApiTokenBanner: it stays gone for
 * this browser session and comes back next login. The key carries the customer
 * AND the level, so switching customer shows the new one's position, and an
 * account that crosses from warning to exhausted says so once more rather than
 * staying silent behind an earlier dismissal.
 *
 * This exists because there is no notification service yet — see the backlog
 * item on consolidating SendGrid into one. When that lands, this banner is the
 * in-app half of the same signal, not a replacement for the email.
 */

const dismissKey = (customerId: string, level: string) =>
  `broadcastAllowanceBanner:${customerId}:${level}`

const BroadcastAllowanceBanner: React.FC<{ customerId: string }> = ({ customerId }) => {
  const [ent, setEnt] = useState<LibraryEntitlement | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!customerId) return
    let live = true
    broadcastService.getEntitlement(customerId)
      .then((res) => { if (live) setEnt(res.data) })
      // Silent: a banner that cannot load its own reason to exist should not
      // put an error over a page that is otherwise fine. The 403 case is the
      // feature gate and the page already renders that.
      .catch(() => { if (live) setEnt(null) })
    return () => { live = false }
  }, [customerId])

  const level = ent?.level ?? 'ok'

  // Re-read on every render of a new level rather than once on mount: the
  // level is not known until the fetch lands, so a mount-time read would
  // always test the wrong key.
  useEffect(() => {
    if (level === 'ok') return
    setDismissed(sessionStorage.getItem(dismissKey(customerId, level)) === 'true')
  }, [customerId, level])

  if (!ent || level === 'ok' || dismissed) return null

  const exhausted = level === 'exhausted'
  const dismiss = () => {
    sessionStorage.setItem(dismissKey(customerId, level), 'true')
    setDismissed(true)
  }

  return (
    <div
      className={`flex items-start gap-2.5 px-3 py-2 text-xs rounded-lg border ${
        exhausted
          ? 'bg-red-50 border-red-200 text-red-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}
      role="status"
    >
      <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {exhausted ? (
          <span>
            <span className="font-semibold">You have used every credit in this period.</span>{' '}
            {ent.used} of {ent.purchased} analysed
            {ent.periodLabel ? ` in ${ent.periodLabel}` : ''}. Contact your account manager to
            add more — broadcasts already published stay available.
          </span>
        ) : (
          <span>
            <span className="font-semibold">
              {ent.remaining} {ent.remaining === 1 ? 'credit' : 'credits'} left in this period.
            </span>{' '}
            {ent.used} of {ent.purchased} used
            {ent.periodLabel ? ` in ${ent.periodLabel}` : ''}. Contact your account manager if you
            need more before the period ends.
          </span>
        )}
      </div>
      <button
        onClick={dismiss}
        className="flex-shrink-0 ml-1 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export default BroadcastAllowanceBanner
